/**
 * AI 議題工作區 — API 服務
 */

import axios from '../api/axios';
import {
  LONG_TIMEOUT_MS,
  ITEM_ANALYZE_TIMEOUT_MS,
  SOURCE_TEST_TIMEOUT_MS,
} from '../api/aiWorkspaceConstants';
import {
  Topic, TopicCreate, TopicUpdate,
  Subject, SubjectCreate, SubjectUpdate, SubjectDraft,
  Audience, AudienceCreate, AudienceUpdate,
  AudienceSuggestion, IncentiveSuggestion,
  TopicSource, SourceCreate, SourceUpdate,
  ResearchItem, ItemCreate, ItemUpdate, SubjectAggregateItem,
  Report, ReportGenerateRequest, ReportUpdate,
  ChatMessage, WorkspaceChatResponse, CollectorStat,
} from '../types/research';

// urlPrecheck 已搬到共用 client；re-export 給舊呼叫端維持兼容
export { urlPrecheck } from '../api/aiWorkspaceClient';
export type { UrlPrecheckResult } from '../api/aiWorkspaceClient';

const BASE = '/api/ee/research';

// ============================================================
// Topics
// ============================================================
export const listTopics = async (params?: {
  is_active?: boolean; search?: string; subject_id?: number;
}): Promise<Topic[]> => {
  const res = await axios.get(`${BASE}/topics`, { params });
  return res.data;
};

export const getTopic = async (id: number): Promise<Topic> => {
  const res = await axios.get(`${BASE}/topics/${id}`);
  return res.data;
};

export const getTopicByCode = async (code: string): Promise<Topic> => {
  const res = await axios.get(`${BASE}/topics/by-code/${code}`);
  return res.data;
};

export const createTopic = async (data: TopicCreate): Promise<Topic> => {
  const res = await axios.post(`${BASE}/topics`, data);
  return res.data;
};

export const updateTopic = async (id: number, data: TopicUpdate): Promise<Topic> => {
  const res = await axios.put(`${BASE}/topics/${id}`, data);
  return res.data;
};

export const deleteTopic = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/topics/${id}`);
};

// ============================================================
// Subjects（主題 — Step 4）
// ============================================================
export const listSubjects = async (params?: {
  is_active?: boolean; status?: string; search?: string;
}): Promise<Subject[]> => {
  const res = await axios.get(`${BASE}/subjects`, { params });
  return res.data;
};

export const getSubject = async (id: number): Promise<Subject> => {
  const res = await axios.get(`${BASE}/subjects/${id}`);
  return res.data;
};

export const listSubjectTopics = async (id: number): Promise<Topic[]> => {
  const res = await axios.get(`${BASE}/subjects/${id}/topics`);
  return res.data;
};

// Layer 2 SOFT 去重：跨旗下議題的素材聚合視角
export const listSubjectItems = async (id: number, params?: {
  ai_status?: string; page?: number; page_size?: number;
}): Promise<SubjectAggregateItem[]> => {
  const res = await axios.get(`${BASE}/subjects/${id}/items`, { params });
  return res.data;
};

// Subject-led 管理（v0.3：主題位階高於議題，從主題挑要哪些議題）
export const replaceSubjectTopics = async (
  subjectId: number,
  topicIds: number[],
  primaryTopicIds: number[],
): Promise<Topic[]> => {
  const res = await axios.put(`${BASE}/subjects/${subjectId}/topics`, {
    topic_ids: topicIds,
    primary_topic_ids: primaryTopicIds,
  });
  return res.data;
};

export const addTopicToSubject = async (
  subjectId: number, topicId: number, isPrimary = false,
): Promise<Topic> => {
  const res = await axios.post(
    `${BASE}/subjects/${subjectId}/topics/${topicId}`,
    null,
    { params: { is_primary: isPrimary } },
  );
  return res.data;
};

export const removeTopicFromSubject = async (
  subjectId: number, topicId: number,
): Promise<void> => {
  await axios.delete(`${BASE}/subjects/${subjectId}/topics/${topicId}`);
};

// ============================================================
// 主題對象 + 誘因（Step 2）
// ============================================================
export const listAudiences = async (subjectId: number,
                                    params?: { is_active?: boolean }): Promise<Audience[]> => {
  const res = await axios.get(`${BASE}/subjects/${subjectId}/audiences`, { params });
  return res.data;
};

export const createAudience = async (subjectId: number,
                                     data: AudienceCreate): Promise<Audience> => {
  const res = await axios.post(`${BASE}/subjects/${subjectId}/audiences`, data);
  return res.data;
};

export const updateAudience = async (audienceId: number,
                                     data: AudienceUpdate): Promise<Audience> => {
  const res = await axios.put(`${BASE}/audiences/${audienceId}`, data);
  return res.data;
};

export const deleteAudience = async (audienceId: number): Promise<void> => {
  await axios.delete(`${BASE}/audiences/${audienceId}`);
};

// AI 建議（preview，不寫 DB）
export const suggestAudiences = async (
  subjectId: number, count = 5,
): Promise<AudienceSuggestion[]> => {
  const res = await axios.post(`${BASE}/subjects/${subjectId}/audiences/suggest`,
                                { count }, { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

export const suggestIncentives = async (
  subjectId: number, audienceName: string,
  persona?: string, count = 6,
): Promise<IncentiveSuggestion[]> => {
  const res = await axios.post(
    `${BASE}/subjects/${subjectId}/audiences/suggest-incentives`,
    { audience_name: audienceName, persona, count },
    { timeout: LONG_TIMEOUT_MS },
  );
  return res.data;
};

// Mode B 一鍵：策略描述 → 完整 SubjectDraft（preview，不寫 DB）
export const draftSubjectFromDescription = async (
  description: string,
): Promise<SubjectDraft> => {
  const res = await axios.post(`${BASE}/subjects/draft-from-description`,
                                { description },
                                { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

// Mode A 問答式：多輪對話累積 SubjectDraft；最終 ready=true 時 state 是完整 draft
export interface DraftConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DraftConversationResponse {
  next_question: string | null;
  ready: boolean;
  state: Partial<SubjectDraft>;
}

export const draftSubjectConversation = async (
  messages: DraftConversationMessage[],
  state?: Partial<SubjectDraft> | null,
): Promise<DraftConversationResponse> => {
  const res = await axios.post(`${BASE}/subjects/draft-conversation`,
                                { messages, state: state ?? null },
                                { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

export const createSubject = async (data: SubjectCreate): Promise<Subject> => {
  const res = await axios.post(`${BASE}/subjects`, data);
  return res.data;
};

export const updateSubject = async (id: number, data: SubjectUpdate): Promise<Subject> => {
  const res = await axios.put(`${BASE}/subjects/${id}`, data);
  return res.data;
};

export const deleteSubject = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/subjects/${id}`);
};

// ============================================================
// Sources
// ============================================================
export const listSources = async (topicId: number): Promise<TopicSource[]> => {
  const res = await axios.get(`${BASE}/topics/${topicId}/sources`);
  return res.data;
};

export const createSource = async (topicId: number, data: SourceCreate): Promise<TopicSource> => {
  const res = await axios.post(`${BASE}/topics/${topicId}/sources`, data);
  return res.data;
};

export const updateSource = async (id: number, data: SourceUpdate): Promise<TopicSource> => {
  const res = await axios.put(`${BASE}/sources/${id}`, data);
  return res.data;
};

export const deleteSource = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/sources/${id}`);
};

export const testSource = async (id: number): Promise<TopicSource> => {
  // 來源測試也可能網路慢
  const res = await axios.post(`${BASE}/sources/${id}/test`, null,
                               { timeout: SOURCE_TEST_TIMEOUT_MS });
  return res.data;
};

// ============================================================
// Items
// ============================================================
export const listItems = async (topicId: number, params?: {
  ai_status?: string; page?: number; page_size?: number;
}): Promise<ResearchItem[]> => {
  const res = await axios.get(`${BASE}/topics/${topicId}/items`, { params });
  return res.data;
};

export const getItem = async (id: number): Promise<ResearchItem> => {
  const res = await axios.get(`${BASE}/items/${id}`);
  return res.data;
};

export const createItem = async (topicId: number, data: ItemCreate): Promise<ResearchItem> => {
  const res = await axios.post(`${BASE}/topics/${topicId}/items`, data);
  return res.data;
};

export const updateItem = async (id: number, data: ItemUpdate): Promise<ResearchItem> => {
  const res = await axios.put(`${BASE}/items/${id}`, data);
  return res.data;
};

export const deleteItem = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/items/${id}`);
};

export const analyzeItem = async (id: number): Promise<ResearchItem> => {
  // 單篇 AI 解析可能 5–30 秒
  const res = await axios.post(`${BASE}/items/${id}/analyze`, null,
                               { timeout: ITEM_ANALYZE_TIMEOUT_MS });
  return res.data;
};

// ============================================================
// Collector
// ============================================================
// 蒐集 + AI 解析每篇可能要數十秒到數分鐘；timeout 從共用框架常數來
export const fetchNow = async (topicId: number,
                               ignoreQuota = false): Promise<CollectorStat> => {
  const res = await axios.post(`${BASE}/topics/${topicId}/fetch_now`,
                               null,
                               { params: { ignore_quota: ignoreQuota }, timeout: LONG_TIMEOUT_MS });
  return res.data;
};

export const fetchAll = async (): Promise<{ topics_processed: number; results: CollectorStat[] }> => {
  const res = await axios.post(`${BASE}/fetch_all`, null, { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

// ============================================================
// Reports
// ============================================================
export const listReports = async (topicId: number, status?: string): Promise<Report[]> => {
  const res = await axios.get(`${BASE}/topics/${topicId}/reports`,
                              { params: status ? { status } : {} });
  return res.data;
};

export const getReport = async (id: number): Promise<Report> => {
  const res = await axios.get(`${BASE}/reports/${id}`);
  return res.data;
};

export const generateReport = async (topicId: number,
                                     data: ReportGenerateRequest): Promise<Report> => {
  // 報告生成可能 30 秒以上（max_tokens=4096）
  const res = await axios.post(`${BASE}/topics/${topicId}/reports/generate`, data,
                               { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

export const updateReport = async (id: number, data: ReportUpdate): Promise<Report> => {
  const res = await axios.put(`${BASE}/reports/${id}`, data);
  return res.data;
};

export const deleteReport = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/reports/${id}`);
};

// ============================================================
// Workspace chat
// ============================================================
export const workspaceChat = async (topicId: number,
                                    messages: ChatMessage[]): Promise<WorkspaceChatResponse> => {
  // 對話本身可能含長篇任務（翻譯/分析整份報告），用長 timeout
  const res = await axios.post(`${BASE}/topics/${topicId}/chat`, { messages },
                               { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

// ============================================================
// AI 助理動作執行（Level 2）
// ============================================================
export interface ExecuteActionResult {
  ok: boolean;
  message: string;
  data?: any;
}

export const executeAction = async (topicId: number,
                                    action: Record<string, any>): Promise<ExecuteActionResult> => {
  // fetch_now / generate_report 等動作可能很久；統一用長 timeout
  const res = await axios.post(`${BASE}/topics/${topicId}/execute_action`, { action },
                               { timeout: LONG_TIMEOUT_MS });
  return res.data;
};

// urlPrecheck 已 re-export 自 ../api/aiWorkspaceClient（見檔案頂部）


// ============================================================
// AI 議題拆解（Step 1 加速器）
// ============================================================
export interface DecomposeTopicResult {
  markdown: string;          // for markmap rendering
  structured: {
    purpose?: string;
    keywords?: string[];
    tag_dimensions?: string[];
    sub_questions?: string[];
    source_types?: string[];
  };
}

export const decomposeTopicSuggestion = async (
  topicName: string,
  description?: string,
): Promise<DecomposeTopicResult> => {
  const res = await axios.post(
    `${BASE}/topics/decompose-suggestion`,
    { topic_name: topicName, description: description || null },
    { timeout: LONG_TIMEOUT_MS },
  );
  return res.data;
};
